// 일일 던전·무한의 탑 판 계획(L9a, PRD 5절): 기존 지역 지도의 마지막 아레나 한 방만 쓰는 StagePlan.
// 새 몹·보스·지도는 없다 — 지역 지도(stageMapOf 1번 또는 지역 지도)와 기존 몹 종류·지역 보스를 그대로 쓴다.
// 판 시작에서만 부르고 같은 키는 같은 객체를 돌려준다(planCache와 같은 규칙, 프레임 루프에서 부르지 않는다).
import { MAPS, REGION_IDS, regionNoOf, type MapDef, type RegionId } from '../levels';
import { REGIONS } from '../regions';
import { stageMapOf } from '../stageMaps';
import { BOSS_DEFS, REGION_BOSSES } from './bosses';
import { stageBossHp, type RoomPlan, type StagePlan } from './stages';
import { TOWER_FLOOR_MAX, TOWER_HIGH_BOSSES, TOWER_HIGH_FROM, TOWER_HP_AT_50, towerHighDmgMul, towerHighHpFactor, towerHighMobLevel, towerHighWaves } from './towerHigh';
import { REGION_HP_MULT, mobLevel } from './world';
import type { DropTableId, RegionNo } from './types';
import type { WaveDef } from '../levels';

export type DailyKind = 'vault' | 'mine' | 'tower';
export const DAILY_KINDS: readonly DailyKind[] = ['vault', 'mine', 'tower'];
/** 황금 보물고 제한 시간(초) */
export const VAULT_SECONDS = 60;
/** 탑 층 배율: 몹 HP 층당 +6%, 몹 피해 층당 +4% (1층 = ×1) */
export const TOWER_HP_STEP = 0.06;
export const TOWER_DMG_STEP = 0.04;
export const towerHpMul = (floor: number): number => 1 + TOWER_HP_STEP * (Math.max(1, floor) - 1);
export const towerDmgMul = (floor: number): number => 1 + TOWER_DMG_STEP * (Math.max(1, floor) - 1);

/** 마을(도착한 가장 높은 마을) → 일일 던전 지역. 지도가 있는 지역(REGION_IDS, C2부터 6개)까지 */
export function dailyRegionOf(town: number): RegionId {
  return REGION_IDS[Math.max(1, Math.min(REGION_IDS.length, Math.floor(town) || 1)) - 1];
}

export const dailyLabel = (kind: DailyKind, floor: number): string =>
  kind === 'vault' ? '황금 보물고' : kind === 'mine' ? '강화석 광산' : '무한의 탑 ' + floor + '층';

const EMPTY_ROOM: RoomPlan = Object.freeze({ kind: 'battle' as const, waves: [] as WaveDef[] });
const cache = new Map<string, StagePlan>();

/** 일일 던전 지도: 그 지역의 1번 스테이지 지도(없으면 지역 지도). 마지막 방(아레나)만 싸운다 */
function dailyMap(region: RegionId): MapDef { return stageMapOf(region, 1) ?? MAPS[region]; }

function build(region: RegionId, kind: DailyKind, floor: number, last: RoomPlan, hpMul: number, dmgMul: number, bossHp: number, bossDmg: number, drops: DropTableId, lvl = 0): StagePlan {
  const map = dailyMap(region), regionNo = regionNoOf(region), info = REGIONS[region];
  const rooms: RoomPlan[] = [];
  for (let i = 0; i < map.rooms.length - 1; i++) rooms.push(EMPTY_ROOM);
  rooms.push(last);
  const hp = Math.round(REGION_HP_MULT[regionNo - 1] * hpMul * 1000) / 1000;
  const dmg = Math.round(info.mobDamageMult * dmgMul * 1000) / 1000;
  return {
    stage: null, region, map, regionNo, index: 0, kind: 'daily', rooms,
    mobHpMult: hp, mobDamageMult: dmg, envDamageMult: Math.round(info.mobDamageMult * 1000) / 1000,
    bossHp, bossDamageMult: bossDmg, bossExtras: true, drops, xpStage: 1,
    diff: 'normal', mobLevel: lvl || mobLevel(regionNo as RegionNo, 'normal'), xpDiff: 1, goldDiff: 1,
    daily: { kind, floor },
  };
}

/** 황금 보물고: 60초 안에 보물 고블린 떼(빠른 고블린)를 전부 잡는다. 몹은 약하고 많다 */
function vaultWaves(regionNo: number): WaveDef[] {
  const n = 8 + regionNo * 2;
  return [{ counts: [n, 0, 0, 0] }, { counts: [n, 0, 0, 0], elites: 3 }];
}
/** 강화석 광산: 수정 골렘(방패 고블린·멧돼지 = 가장 단단한 기존 몹) 3웨이브 */
function mineWaves(regionNo: number): WaveDef[] {
  const s = regionNo >= 2 ? 1 : 0;
  return [
    { counts: [0, 5, 0, 0], elites: 1 },
    { counts: [0, 4, 3, s], elites: 2 },
    { counts: [0, 5, 4, s], elites: 3 },
  ];
}
/** 탑 일반 층 웨이브: 층이 오를수록 마릿수가 조금씩 는다 */
function towerWaves(regionNo: number, floor: number): WaveDef[] {
  const s = regionNo >= 2 ? 1 + Math.floor(floor / 12) : 0;
  const a = 4 + Math.floor(floor / 5), b = 2 + Math.floor(floor / 8), c = 1 + Math.floor(floor / 10);
  return [
    { counts: [a, b, c, s] },
    { counts: [a + 2, b + 1, c + 1, s], elites: 1 + Math.floor(floor / 10) },
  ];
}

/** 탑 보스 층(10·20·…·50)의 보스: 10·30·50층 중간보스급, 20·40층 지역보스 — 그 지역의 기존 보스 */
export function towerBossOf(regionNo: number, floor: number): { key: typeof REGION_BOSSES[number]['mid']; tier: 'mid' | 'final' } {
  const tier: 'mid' | 'final' = floor % 20 === 0 ? 'final' : 'mid';
  return { key: REGION_BOSSES[regionNo - 1][tier], tier };
}

/** 판 계획. town = 도착한 가장 높은 마을(지역 = min(4, town)), floor = 탑 층(보물고·광산은 0) */
export function dailyPlan(kind: DailyKind, town: number, floor = 0): StagePlan {
  const region = dailyRegionOf(town), regionNo = regionNoOf(region);
  const f = kind === 'tower' ? Math.max(1, Math.min(TOWER_FLOOR_MAX, Math.floor(floor))) : 0;
  // 51층 이상은 마을과 상관없이 같은 판(지도는 지금 r4, C2가 지역5·6 지도를 붙이면 towerHigh의 보스·지도만 바꾼다)
  const high = kind === 'tower' && f >= TOWER_HIGH_FROM;
  const key = high ? 'tower:high:' + f : kind + ':' + region + ':' + f;
  const hit = cache.get(key);
  if (hit) return hit;
  let plan: StagePlan;
  if (kind === 'vault') {
    plan = build(region, kind, 0, { kind: 'battle', waves: vaultWaves(regionNo) }, 0.5, 0.6, 0, 1, 'drop_normal');
  } else if (kind === 'mine') {
    plan = build(region, kind, 0, { kind: 'battle', waves: mineWaves(regionNo) }, 1.5, 0.8, 0, 1, 'drop_normal');
  } else if (high) {
    plan = towerHighPlan(f);
  } else if (f % 10 === 0) {
    const { key: boss, tier } = towerBossOf(regionNo, f);
    const hp = Math.round(stageBossHp(regionNo, 10, tier) * towerHpMul(f) / 100) * 100;
    const info = REGIONS[region];
    plan = build(region, kind, f, { kind: 'boss', waves: [], boss }, towerHpMul(f), towerDmgMul(f), hp,
      Math.round(info.mobDamageMult * towerDmgMul(f) * BOSS_DEFS[boss].dmg * 1000) / 1000, tier === 'final' ? 'drop_final' : 'drop_mid');
  } else {
    plan = build(region, kind, f, { kind: 'battle', waves: towerWaves(regionNo, f) }, towerHpMul(f), towerDmgMul(f), 0, 1, 'drop_normal');
  }
  cache.set(key, plan);
  return plan;
}

/** 탑 51~100층 판(content/towerHigh.ts 곡선): 지도는 지역4, 몹 HP는 50층 총량에서 기하 곡선으로, 피해는 직선으로, 몹 레벨은 68/80 */
function towerHighPlan(f: number): StagePlan {
  const region: RegionId = 'r4', info = REGIONS[region];
  const hpMul = TOWER_HP_AT_50 * towerHighHpFactor(f), dmgMul = towerHighDmgMul(f), lvl = towerHighMobLevel(f);
  const boss = TOWER_HIGH_BOSSES[f];
  if (boss) {
    const tier: 'mid' | 'final' = f % 20 === 0 ? 'final' : 'mid';
    const hp = Math.round(stageBossHp(4, 10, tier) * hpMul / 100) * 100;
    return build(region, 'tower', f, { kind: 'boss', waves: [], boss }, hpMul, dmgMul, hp,
      Math.round(info.mobDamageMult * dmgMul * BOSS_DEFS[boss].dmg * 1000) / 1000, tier === 'final' ? 'drop_final' : 'drop_mid', lvl);
  }
  return build(region, 'tower', f, { kind: 'battle', waves: towerHighWaves(f) }, hpMul, dmgMul, 0, 1, 'drop_normal', lvl);
}
